import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  BookOpen,
  ChevronRight,
  HelpCircle,
  RefreshCw,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { AppLayout } from "../components/layout/AppLayout.js";
import { Header } from "../components/layout/Header.js";
import { type SurvivalCurve, type SurvivalPoint, employeesApi } from "../lib/api.js";

type GroupBy = "department" | "category" | "cohort_year";

const GROUP_TABS: { key: GroupBy; label: string }[] = [
  { key: "category", label: "Nach Kategorie" },
  { key: "department", label: "Nach Abteilung" },
  { key: "cohort_year", label: "Nach Eintrittsjahr" },
];

const FALLBACK_COLORS = [
  // Kategoriale Serien in fester Token-Reihenfolge — Kurven behalten ihre Farbe
  // auch dann, wenn beim Filtern einzelne Serien wegfallen.
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

function getColor(curve: SurvivalCurve, index: number): string {
  return curve.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

// ── Help System ──────────────────────────────────────────────────────────────

interface HelpEntry {
  id: string;
  title: string;
  content: string;
  learnMore?: string;
}

const HELP_CONTENT: Record<string, HelpEntry[]> = {
  chart: [
    {
      id: "what-is-kaplan-meier",
      title: "Was zeigt diese Kurve?",
      content:
        "Die Kaplan-Meier-Kurve zeigt den Anteil der Mitarbeiter, die nach einer bestimmten Anzahl von Monaten noch im Unternehmen sind. Eine Kurve, die schnell abfällt, weist auf hohe Fluktuation hin — eine flache Kurve bedeutet, dass Mitarbeiter lange bleiben.",
    },
    {
      id: "how-to-read",
      title: "Wie lese ich die Kurve?",
      content:
        "Die X-Achse zeigt die Monate seit Eintritt, die Y-Achse den Prozentsatz noch aktiver Mitarbeiter. Bei 50% hat die Hälfte der Gruppe das Unternehmen bereits verlassen. Die gestrichelte Linie bei 50% markiert diesen Median — wo eine Kurve diese Linie schneidet, ist die typische Verweildauer dieser Gruppe.",
    },
    {
      id: "at-risk",
      title: 'Was bedeutet "aktiv" im Tooltip?',
      content:
        "Die Zahl in Klammern zeigt, wie viele Mitarbeiter den jeweiligen Zeitpunkt überhaupt erreicht haben (sogenanntes 'At-Risk-Set'). Bei neueren Kohorten sinkt diese Zahl bei späteren Milestones, weil viele noch gar nicht lang genug angestellt sind.",
    },
  ],
  grouping: [
    {
      id: "category-meaning",
      title: "Nach Kategorie",
      content:
        "Unterteilt nach Beschäftigungsart: Senior Consultants, TNW-Consultants (Talentnetzwerk) und interne Mitarbeiter. So sehen Sie, ob bestimmte Mitarbeitergruppen schneller abwandern als andere.",
    },
    {
      id: "department-meaning",
      title: "Nach Abteilung",
      content:
        "Vergleicht die Top-5-Abteilungen nach Mitarbeiterzahl. Ermöglicht es, Abteilungen mit überdurchschnittlicher Fluktuation zu identifizieren und gezielt Maßnahmen zu ergreifen.",
    },
    {
      id: "cohort-meaning",
      title: "Nach Eintrittsjahr",
      content:
        "Gruppiert Mitarbeiter nach ihrem Eintrittsjahr. Zeigt, ob bestimmte Jahrgänge besser oder schlechter gehalten werden — z.B. ob Onboarding-Verbesserungen Wirkung zeigen.",
    },
  ],
  median: [
    {
      id: "median-meaning",
      title: "Was ist die Median-Überlebenszeit?",
      content:
        "Der Zeitpunkt, an dem 50% einer Gruppe das Unternehmen verlassen haben. Ein Median von 24 Monaten bedeutet: Die Hälfte der Mitarbeiter geht innerhalb von 2 Jahren. Je höher der Wert, desto besser die Retention.",
    },
    {
      id: "median-not-reached",
      title: 'Was bedeutet "nicht erreicht"?',
      content:
        "Wenn der Median als '—' angezeigt wird, hat die Gruppe die 50%-Schwelle im Beobachtungszeitraum nie unterschritten. Das ist ein positives Signal — mehr als die Hälfte der Mitarbeiter ist langfristig geblieben.",
    },
  ],
};

function HelpButton({ onClick, active }: { onClick: () => void; active: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all"
      style={{
        background: active ? "var(--accent-dim)" : "transparent",
        color: active ? "var(--accent)" : "var(--text-muted)",
        border: `1px solid ${active ? "var(--accent)" : "var(--border)"}`,
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = "var(--border-light)";
          e.currentTarget.style.color = "var(--text-secondary)";
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.borderColor = "var(--border)";
          e.currentTarget.style.color = "var(--text-muted)";
        }
      }}
    >
      <HelpCircle size={12} />
      Hilfe
    </button>
  );
}

function HelpPanel({ entries, onClose }: { entries: HelpEntry[]; onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [expandedId, setExpandedId] = useState<string | null>(entries[0]?.id ?? null);

  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.style.opacity = "0";
      panelRef.current.style.transform = "translateY(-4px)";
      requestAnimationFrame(() => {
        if (panelRef.current) {
          panelRef.current.style.transition = "opacity 0.2s ease-out, transform 0.2s ease-out";
          panelRef.current.style.opacity = "1";
          panelRef.current.style.transform = "translateY(0)";
        }
      });
    }
  }, []);

  return (
    <div
      ref={panelRef}
      className="rounded-lg mb-3 overflow-hidden"
      style={{
        background: "var(--bg-surface)",
        border: "1px solid var(--border)",
        boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
      }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-4 py-2.5"
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <BookOpen size={12} style={{ color: "var(--accent)" }} />
        <span
          className="text-xs font-semibold tracking-wide uppercase"
          style={{ color: "var(--text-muted)" }}
        >
          Erklärung
        </span>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto p-1 rounded transition-colors"
          style={{ color: "var(--text-muted)" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "var(--text-secondary)";
            e.currentTarget.style.background = "var(--bg-hover)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "var(--text-muted)";
            e.currentTarget.style.background = "transparent";
          }}
        >
          <X size={12} />
        </button>
      </div>

      {/* Accordion Items */}
      <div className="px-3 py-2">
        {entries.map((entry) => {
          const isOpen = expandedId === entry.id;
          return (
            <div key={entry.id} style={{ borderBottom: "1px solid var(--border)" }}>
              <button
                type="button"
                onClick={() => setExpandedId(isOpen ? null : entry.id)}
                className="w-full flex items-center gap-2 py-2.5 px-1 text-left transition-colors"
                style={{ color: isOpen ? "var(--text-primary)" : "var(--text-secondary)" }}
                onMouseEnter={(e) => {
                  if (!isOpen) e.currentTarget.style.color = "var(--text-primary)";
                }}
                onMouseLeave={(e) => {
                  if (!isOpen) e.currentTarget.style.color = "var(--text-secondary)";
                }}
              >
                <ChevronRight
                  size={11}
                  style={{
                    color: isOpen ? "var(--accent)" : "var(--text-muted)",
                    transform: isOpen ? "rotate(90deg)" : "rotate(0)",
                    transition: "transform 0.15s ease",
                    flexShrink: 0,
                  }}
                />
                <span className="text-xs font-medium">{entry.title}</span>
              </button>
              <div
                style={{
                  maxHeight: isOpen ? 200 : 0,
                  overflow: "hidden",
                  transition: "max-height 0.2s ease",
                }}
              >
                <p
                  className="text-xs leading-relaxed px-1 pb-3"
                  style={{
                    color: "var(--text-secondary)",
                    paddingLeft: 20,
                  }}
                >
                  {entry.content}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Inline Contextual Hint ───────────────────────────────────────────────────

function InlineHint({ text }: { text: string }) {
  const [visible, setVisible] = useState(false);

  return (
    <span className="relative inline-flex items-center ml-1">
      <button
        type="button"
        onClick={() => setVisible(!visible)}
        className="inline-flex items-center justify-center rounded-full transition-all"
        style={{
          width: 14,
          height: 14,
          background: visible ? "var(--accent-dim)" : "transparent",
          color: visible ? "var(--accent)" : "var(--text-muted)",
          border: `1px solid ${visible ? "var(--accent)" : "transparent"}`,
        }}
        onMouseEnter={(e) => {
          if (!visible) e.currentTarget.style.color = "var(--text-secondary)";
        }}
        onMouseLeave={(e) => {
          if (!visible)
            e.currentTarget.style.color = visible ? "var(--accent)" : "var(--text-muted)";
        }}
      >
        <HelpCircle size={10} />
      </button>
      {visible && (
        <div
          className="absolute left-6 top-1/2 -translate-y-1/2 z-50 rounded-md px-3 py-2 text-xs leading-relaxed"
          style={{
            background: "var(--bg-surface)",
            border: "1px solid var(--border-light)",
            color: "var(--text-secondary)",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            width: 260,
            animation: "pageEnter 0.15s ease-out forwards",
          }}
        >
          {text}
          <div
            className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45"
            style={{
              width: 6,
              height: 6,
              background: "var(--bg-surface)",
              borderLeft: "1px solid var(--border-light)",
              borderBottom: "1px solid var(--border-light)",
            }}
          />
        </div>
      )}
    </span>
  );
}

// ── Custom Tooltip ────────────────────────────────────────────────────────────

interface ChartPayloadEntry {
  name: string;
  value: number;
  color: string;
  payload: SurvivalPoint & { groupLabel?: string };
}

function SurvivalTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div
      style={{
        background: "var(--bg-surface)",
        border: "1px solid var(--border)",
        borderRadius: "6px",
        padding: "8px 12px",
        fontSize: "11px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
        minWidth: 180,
      }}
    >
      <div className="font-mono-data font-semibold mb-1.5" style={{ color: "var(--text-muted)" }}>
        Monat {label}
      </div>
      {(payload as ChartPayloadEntry[]).map((entry) => (
        <div key={entry.name} className="flex items-center gap-2 mb-1">
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: entry.color,
              flexShrink: 0,
            }}
          />
          <span style={{ color: "var(--text-secondary)", flex: 1 }}>{entry.name}</span>
          <span className="font-mono-data font-semibold" style={{ color: entry.color }}>
            {entry.value.toFixed(1)}%
          </span>
          {entry.payload.atRisk !== undefined && (
            <span style={{ color: "var(--text-muted)" }}>({entry.payload.atRisk} aktiv)</span>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Median Survival Table ─────────────────────────────────────────────────────

function MedianTable({ curves }: { curves: SurvivalCurve[] }) {
  if (curves.length === 0) return null;

  return (
    <div className="card overflow-hidden">
      <div
        className="px-4 py-2.5 flex items-center gap-2 text-xs font-medium"
        style={{
          borderBottom: "1px solid var(--border)",
          color: "var(--text-muted)",
        }}
      >
        <TrendingUp size={12} />
        MEDIAN-ÜBERLEBENSZEIT
        <InlineHint text="Der Zeitpunkt, an dem 50% einer Gruppe ausgeschieden sind. '—' bedeutet: Mehr als die Hälfte ist langfristig geblieben." />
      </div>
      <div
        className="grid text-xs font-medium px-4 py-2"
        style={{
          gridTemplateColumns: "1fr 140px 80px",
          color: "var(--text-muted)",
          borderBottom: "1px solid var(--border)",
          letterSpacing: "0.04em",
        }}
      >
        <span>GRUPPE</span>
        <span>MEDIAN (MONATE)</span>
        <span className="text-right">SIGNALFARBE</span>
      </div>
      {[...curves]
        .sort((a, b) => {
          if (a.medianSurvivalMonths === null) return 1;
          if (b.medianSurvivalMonths === null) return -1;
          return b.medianSurvivalMonths - a.medianSurvivalMonths;
        })
        .map((curve, i) => (
          <div
            key={curve.groupLabel}
            className="grid items-center px-4 py-2.5 text-sm transition-colors"
            style={{
              gridTemplateColumns: "1fr 140px 80px",
              borderBottom: "1px solid var(--border)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--bg-hover)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <span style={{ color: "var(--text-primary)" }}>{curve.groupLabel}</span>
            <span className="font-mono-data" style={{ color: "var(--text-secondary)" }}>
              {curve.medianSurvivalMonths !== null
                ? `${curve.medianSurvivalMonths} Monate`
                : "— (nicht erreicht)"}
            </span>
            <div className="flex justify-end">
              <span
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  background: getColor(curve, i),
                }}
              />
            </div>
          </div>
        ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function TenureAnalysis() {
  const [groupBy, setGroupBy] = useState<GroupBy>("category");
  // Nur bei Gruppierung nach Abteilung relevant:
  // "exit" = heutige/eingefrorene Abteilung (bisheriges Verhalten),
  // "entry" = Abteilung zum Eintrittsdatum (aus der Org-Historie).
  const [deptPerspective, setDeptPerspective] = useState<"entry" | "exit">("exit");
  const [showChartHelp, setShowChartHelp] = useState(false);
  const [showGroupHelp, setShowGroupHelp] = useState(false);
  const [showMedianHelp, setShowMedianHelp] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["survival-curves", groupBy, deptPerspective],
    queryFn: () =>
      employeesApi.getSurvivalCurves(
        groupBy,
        groupBy === "department" ? deptPerspective : undefined,
      ),
    staleTime: 10 * 60_000,
    retry: 1,
  });

  const { data: attritionData } = useQuery({
    queryKey: ["attrition-trend"],
    queryFn: employeesApi.getAttritionTrend,
    staleTime: 10 * 60_000,
  });

  const curves = data?.curves ?? [];

  // Build merged dataset for Recharts: one object per x-tick, keyed by groupLabel
  type ChartRow = { month: number } & Record<string, number | undefined>;
  const chartData = (() => {
    if (curves.length === 0) return [];
    // Collect all unique month values
    const monthSet = new Set<number>();
    for (const curve of curves) {
      for (const pt of curve.points) {
        monthSet.add(pt.monthsSinceHire);
      }
    }
    const months = Array.from(monthSet).sort((a, b) => a - b);
    return months.map((m) => {
      const row: ChartRow = { month: m };
      for (const curve of curves) {
        const pt = curve.points.find((p) => p.monthsSinceHire === m);
        if (pt) row[curve.groupLabel] = pt.survivorPct;
      }
      return row;
    });
  })();

  return (
    <AppLayout>
      <Header title="Betriebszugehörigkeit" subtitle="Überlebenskurven und Verweildauer-Kohorten" />

      <div className="flex-1 overflow-y-auto px-6 py-5 page-enter">
        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-5">
          {/* Group-by tabs */}
          <div className="flex gap-1">
            {GROUP_TABS.map(({ key, label }) => (
              <button
                type="button"
                key={key}
                onClick={() => setGroupBy(key)}
                className="px-3 py-1.5 rounded text-xs font-medium transition-colors"
                style={{
                  background: groupBy === key ? "var(--accent-dim)" : "transparent",
                  color: groupBy === key ? "var(--accent)" : "var(--text-muted)",
                  border: `1px solid ${groupBy === key ? "var(--accent)" : "var(--border)"}`,
                }}
              >
                {label}
              </button>
            ))}
            <HelpButton onClick={() => setShowGroupHelp(!showGroupHelp)} active={showGroupHelp} />
          </div>

          {/* Abteilungs-Perspektive (nur bei Gruppierung nach Abteilung) */}
          {groupBy === "department" && (
            <div
              className="flex rounded-md overflow-hidden"
              style={{ border: "1px solid var(--border)" }}
            >
              {[
                { key: "exit" as const, label: "Austritts-Abteilung" },
                { key: "entry" as const, label: "Eintritts-Abteilung" },
              ].map(({ key, label }) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => setDeptPerspective(key)}
                  className="px-3 py-1.5 text-xs font-medium transition-colors"
                  title={
                    key === "entry"
                      ? "Wie lange bleiben Leute, die in dieser Abteilung anfangen? (aus der Org-Historie)"
                      : "Aus welcher Abteilung verlassen Leute die Firma? (heutige bzw. eingefrorene Abteilung)"
                  }
                  style={{
                    background: deptPerspective === key ? "var(--accent)" : "transparent",
                    color: deptPerspective === key ? "#fff" : "var(--text-secondary)",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors"
            style={{
              border: "1px solid var(--border)",
              color: "var(--text-muted)",
              background: "transparent",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--border-light)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--border)";
            }}
          >
            <RefreshCw size={11} className={isFetching ? "animate-spin" : ""} />
            {isFetching ? "Lädt…" : "Aktualisieren"}
          </button>
        </div>

        {/* Grouping Help Panel */}
        {showGroupHelp && (
          <HelpPanel entries={HELP_CONTENT.grouping} onClose={() => setShowGroupHelp(false)} />
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center h-56 gap-3">
            <RefreshCw size={18} className="animate-spin" style={{ color: "var(--accent)" }} />
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              Überlebenskurven werden berechnet…
            </p>
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="card flex items-center gap-2 px-4 py-3 mb-4">
            <AlertCircle size={14} style={{ color: "var(--risk-high)" }} />
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {error instanceof Error
                ? error.message
                : "Überlebenskurven-Daten konnten nicht geladen werden."}
            </p>
          </div>
        )}

        {/* No data placeholder */}
        {!isLoading && !isError && curves.length === 0 && (
          <div className="card flex flex-col items-center justify-center h-56 gap-3">
            <Users size={28} style={{ color: "var(--text-muted)", opacity: 0.3 }} />
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              Keine Daten für diese Gruppierung verfügbar.
            </p>
          </div>
        )}

        {/* Chart */}
        {!isLoading && !isError && curves.length > 0 && (
          <>
            <div className="card px-4 py-4 mb-4">
              {/* Chart header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div>
                    <p
                      className="text-xs font-medium tracking-widest uppercase"
                      style={{ color: "var(--text-muted)" }}
                    >
                      ÜBERLEBENSKURVEN (KAPLAN-MEIER)
                    </p>
                    <p
                      className="text-[10px] mt-0.5"
                      style={{ color: "var(--text-muted)", opacity: 0.7 }}
                    >
                      Y-Achse: Anteil noch aktiver Mitarbeiter · X-Achse: Monate seit Eintritt
                    </p>
                  </div>
                  <HelpButton
                    onClick={() => setShowChartHelp(!showChartHelp)}
                    active={showChartHelp}
                  />
                </div>
                {/* Legend */}
                <div className="flex flex-wrap gap-3 justify-end max-w-sm">
                  {curves.map((curve, i) => (
                    <div key={curve.groupLabel} className="flex items-center gap-1.5">
                      <span
                        style={{
                          display: "inline-block",
                          width: 20,
                          height: 2,
                          background: getColor(curve, i),
                          borderRadius: 1,
                        }}
                      />
                      <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                        {curve.groupLabel}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Chart Help Panel */}
              {showChartHelp && (
                <HelpPanel entries={HELP_CONTENT.chart} onClose={() => setShowChartHelp(false)} />
              )}

              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke="var(--border)" opacity={0.6} />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 10, fill: "var(--text-muted)" as string }}
                    tickFormatter={(v: number) => `${v}M`}
                    ticks={[0, 6, 12, 18, 24, 36, 48, 60]}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 10, fill: "var(--text-muted)" as string }}
                    tickFormatter={(v: number) => `${v}%`}
                    domain={[0, 100]}
                    width={36}
                  />
                  {/* 50% reference line for median */}
                  <ReferenceLine
                    y={50}
                    stroke="var(--border-light)"
                    strokeDasharray="4 4"
                    opacity={0.5}
                    label={{
                      value: "50% Median",
                      position: "insideTopRight",
                      fontSize: 9,
                      fill: "var(--text-muted)",
                    }}
                  />
                  <Tooltip content={<SurvivalTooltip />} />
                  {curves.map((curve, i) => (
                    <Line
                      key={curve.groupLabel}
                      type="monotone"
                      dataKey={curve.groupLabel}
                      stroke={getColor(curve, i)}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{
                        r: 4,
                        fill: getColor(curve, i),
                        stroke: "var(--bg-card)",
                        strokeWidth: 2,
                      }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Median Help Panel */}
            {showMedianHelp && (
              <HelpPanel entries={HELP_CONTENT.median} onClose={() => setShowMedianHelp(false)} />
            )}

            {/* Median table with help button */}
            <div className="relative">
              <div className="absolute right-3 top-2 z-10">
                <HelpButton
                  onClick={() => setShowMedianHelp(!showMedianHelp)}
                  active={showMedianHelp}
                />
              </div>
              <MedianTable curves={curves} />
            </div>
          </>
        )}

        {/* ── Attrition Chart ─────────────────────────────────────────── */}
        {attritionData && attritionData.quarters.length > 0 && (
          <div className="card px-4 py-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <p
                className="text-xs font-medium tracking-widest uppercase"
                style={{ color: "var(--text-muted)" }}
              >
                FLUKTUATION JE QUARTAL
              </p>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={attritionData.quarters.slice(-12)}
                margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
              >
                <CartesianGrid strokeDasharray="2 4" stroke="var(--border)" opacity={0.6} />
                <XAxis
                  dataKey="quarter"
                  tick={{ fontSize: 10, fill: "var(--text-muted)" as string }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: "var(--text-muted)" as string }}
                  tickLine={false}
                  axisLine={false}
                  width={28}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--bg-card)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "var(--text-primary)",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  dataKey="hires"
                  name="Eintritte"
                  fill="var(--risk-low)"
                  radius={[3, 3, 0, 0]}
                />
                <Bar
                  dataKey="exits"
                  name="Austritte"
                  fill="var(--risk-high)"
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
